/**
 * Pure helpers for the encounter telemedicine panel (PROD-1276).
 *
 * Faithful to Flutter `encounter_screen.dart`: the video is shown only when the
 * appointment location is "Virtual" (app_constants `appointmentLocationVirtual`),
 * and the iframe URL is `${videoCallUrlBase}/?AppointmentId=&ProviderId=`
 * (encounter_screen.dart:920). ProviderId is the clinician on the call
 * (nurse → practitionerId, else the user id) — resolved by the caller.
 */

/** Flutter `appointmentLocationVirtual`. */
export const VIRTUAL_LOCATION = 'Virtual';

/**
 * The clinician id to stamp on the telemedicine session (Flutter
 * encounter_screen.dart:920): a nurse joins as the practitioner they assist
 * (`practitionerId`), everyone else as themselves (`id`).
 */
export function televisitProviderId(opts: {
  isNurse: boolean;
  id?: string | null;
  practitionerId?: string | null;
}): string | undefined {
  return (opts.isNurse ? opts.practitionerId : opts.id) ?? undefined;
}

/** True when the appointment/encounter location denotes a telehealth visit. */
export function isVirtualLocation(locationName: string | null | undefined): boolean {
  return (locationName ?? '').trim().toLowerCase() === VIRTUAL_LOCATION.toLowerCase();
}

/**
 * Build the telemedicine iframe URL (Flutter parity). Returns null when either
 * id is missing — the panel must not load a half-formed URL.
 */
export function buildVideoCallUrl(
  base: string,
  appointmentId: string | null | undefined,
  providerId: string | null | undefined,
): string | null {
  // Trim before validating so whitespace-only values can't form a broken URL.
  const b = (base ?? '').trim();
  const appt = (appointmentId ?? '').trim();
  const prov = (providerId ?? '').trim();
  if (!b || !appt || !prov) return null;
  const root = b.replace(/\/+$/, '');
  return `${root}/?AppointmentId=${encodeURIComponent(appt)}&ProviderId=${encodeURIComponent(prov)}`;
}
